import React, { FC, useMemo } from 'react';

import { useCart } from 'hooks/use-cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';

import { LoyaltyRewardsEmptyScreen } from './empty-screen/loyalty-rewards-empty-screen';
import { LoyaltyRewardsCategory } from './loyalty-rewards-category';
import { ILoyaltyRewardsList, NonNullableRewardCategory } from './types';
import { useAppliedRewards } from './use-applied-reward';
import { buildRewardsFromCategory } from './utils';

export const LoyaltyRewardsList: FC<ILoyaltyRewardsList> = ({
  handleOnPress,
  rewardCategories,
  engineRewardsMap,
  rewardsEvaluationMap,
  sanityRewardsMap,
}) => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const { cartEntries } = useCart();
  const appliedRewards = useAppliedRewards();
  const { rewardsCooldown } = useLoyaltyContext();

  // Find first unavailable category to display progress meter
  const firstUnavailableCategory = rewardCategories.find((category: NonNullableRewardCategory) => {
    if (!category.rewards?.length || !category.label?.en) {
      return false;
    }
    const engineRewardItem = engineRewardsMap[category?.rewards[0]?.loyaltyEngineId || ''];
    if (engineRewardItem.locked) {
      return true;
    }
    return false;
  });

  const renderedCategories = useMemo(
    () =>
      rewardCategories.map((category: NonNullableRewardCategory) => {
        // Skip empty categories (without rewards or label)
        if (!category.rewards?.length || !category.label?.en) {
          return null;
        }

        const rewards = buildRewardsFromCategory(category, {
          sanityRewardsMap,
          rewardsEvaluationMap,
          cartEntries,
          // When Unstacking is disabled, proceed as if no reward was ever applied
          appliedRewards: enableRewardUnstacking ? appliedRewards : undefined,
          rewardsCooldown,
        });

        // We want to skip the whole category if it doesn't have available rewards
        if (!rewards?.length) {
          return null;
        }

        return (
          <LoyaltyRewardsCategory
            key={category.label.en}
            rewards={rewards}
            category={category}
            firstUnavailableCategory={firstUnavailableCategory}
            engineRewardsMap={engineRewardsMap}
            handleOnPress={handleOnPress}
          />
        );
      }),
    [
      appliedRewards,
      cartEntries,
      enableRewardUnstacking,
      engineRewardsMap,
      firstUnavailableCategory,
      handleOnPress,
      rewardCategories,
      rewardsCooldown,
      rewardsEvaluationMap,
      sanityRewardsMap,
    ]
  );

  if (renderedCategories.every(category => !category)) {
    return <LoyaltyRewardsEmptyScreen />;
  }

  return renderedCategories;
};
